import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, Phone, UserCircle, Briefcase, ChevronRight, Loader2, Check } from 'lucide-react';
import apiClient from '../api/api';

function RegisterPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [contact_No, setContactNo] = useState('');
  const [role, setRole] = useState('worker'); // 'worker' or 'buyer'
  
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!password || !contact_No || !role) {
        setError('Password, contact number, and role are required.');
        setLoading(false);
        return;
    }

    try {
      const response = await apiClient.post('/auth/register', { 
          email, 
          password, 
          phone: contact_No,
          userType: role 
      });

      if (response.status === 201) {
        navigate('/verify-otp', { state: { login: contact_No } });
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const RoleOption = ({ value, label, icon: Icon, description }) => (
    <div 
      onClick={() => setRole(value)}
      className={`relative cursor-pointer p-4 rounded-xl border transition-all duration-200 flex flex-col items-center text-center gap-2 group ${
        role === value 
          ? 'bg-emerald-50 border-emerald-500 ring-1 ring-emerald-500' 
          : 'bg-white border-slate-200 hover:border-emerald-300 hover:shadow-md'
      }`}
    >
      <div className={`p-3 rounded-full transition-colors ${role === value ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-500 group-hover:bg-emerald-100 group-hover:text-emerald-600'}`}>
        <Icon className="w-6 h-6" />
      </div>
      <div>
        <h4 className={`font-bold ${role === value ? 'text-emerald-800' : 'text-slate-700'}`}>{label}</h4>
        <p className="text-xs text-slate-500 mt-1">{description}</p>
      </div>
      {role === value && (
        <div className="absolute top-3 right-3 text-emerald-600">
          <Check className="w-4 h-4" />
        </div>
      )}
    </div>
  );
  
  return (
    <div className="min-h-screen flex bg-slate-50 relative overflow-hidden">
       {/* Background Decoration */}
       <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-1/4 -right-1/4 w-3/4 h-3/4 bg-emerald-100/50 rounded-full blur-3xl opacity-60" />
        <div className="absolute bottom-0 left-0 w-full h-1/2 bg-gradient-to-t from-teal-50 to-transparent" />
      </div>

      <div className="container mx-auto flex items-center justify-center p-4 z-10">
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-5xl bg-white/80 backdrop-blur-xl border border-white/60 rounded-3xl shadow-2xl shadow-slate-200/50 overflow-hidden flex flex-col md:flex-row"
        >
          
          {/* Left Side: Visuals */}
          <div className="hidden md:flex md:w-5/12 bg-gradient-to-br from-emerald-600 to-teal-700 p-12 flex-col justify-between relative overflow-hidden text-white">
            <div className="relative z-10">
              <h2 className="text-3xl font-bold mb-4 leading-tight">Join the Future of<br/><span className="text-emerald-200">Professional Labor</span></h2>
              <p className="text-emerald-100 leading-relaxed">Connect with skilled professionals and verified opportunities across the country. Your journey starts with Service24.</p>
            </div>
            
            <div className="relative z-10 space-y-6 mt-12">
              <div className="flex items-center gap-4 text-emerald-50">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <UserCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-white">Create Profile</h4>
                  <p className="text-sm opacity-80">Showcase your skills or needs</p>
                </div>
              </div>
              <div className="flex items-center gap-4 text-emerald-50">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <Briefcase className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-white">Get Hired / Hire</h4>
                  <p className="text-sm opacity-80">Seamless connection & payments</p>
                </div>
              </div>
            </div>

            {/* Decorative circles */}
            <div className="absolute top-1/2 right-0 transform translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
          </div>

          {/* Right Side: Form */}
          <div className="w-full md:w-7/12 p-8 sm:p-12 bg-white">
            <div className="max-w-md mx-auto">
              <div className="text-center mb-8 md:text-left">
                <h2 className="text-2xl font-bold text-slate-800 mb-2">Create Account</h2>
                <p className="text-slate-500">Choose your role to get started</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                
                {/* Role Selection */}
                <div className="grid grid-cols-2 gap-4">
                  <RoleOption 
                    value="worker" 
                    label="Worker" 
                    icon={UserCircle} 
                    description="I want to find work" 
                  />
                  <RoleOption 
                    value="buyer" 
                    label="Buyer/Owner" 
                    icon={Briefcase} 
                    description="I want to hire people" 
                  />
                </div>
                
                <div className="space-y-4">
                   <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-colors group-focus-within:text-emerald-600 text-slate-400">
                      <Phone className="h-5 w-5" />
                    </div>
                    <input 
                      type="text" 
                      placeholder="Contact Number" 
                      value={contact_No} 
                      onChange={(e) => setContactNo(e.target.value)}
                      className="w-full pl-11 pr-4 py-3.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all duration-200" 
                      required 
                    />
                  </div>

                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-colors group-focus-within:text-emerald-600 text-slate-400">
                      <Mail className="h-5 w-5" />
                    </div>
                    <input 
                      type="email" 
                      placeholder="Email Address (Optional)" 
                      value={email} 
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-11 pr-4 py-3.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all duration-200" 
                    />
                  </div>

                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-colors group-focus-within:text-emerald-600 text-slate-400">
                      <Lock className="h-5 w-5" />
                    </div>
                    <input 
                      type="password" 
                      placeholder="Password" 
                      value={password} 
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full pl-11 pr-4 py-3.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all duration-200" 
                      required 
                    />
                  </div>
                </div>

                {error && (
                  <motion.div 
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="p-3 rounded-lg bg-red-50 border border-red-100 text-red-600 text-sm text-center font-medium"
                  >
                    {error}
                  </motion.div>
                )}

                <button 
                  type="submit" 
                  className="w-full bg-gradient-to-r from-emerald-600 to-teal-500 hover:from-emerald-500 hover:to-teal-400 text-white font-bold py-3.5 px-4 rounded-xl shadow-lg shadow-emerald-500/30 transition-all duration-200 transform hover:scale-[1.02] disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2 group" 
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      <span>Creating Account...</span>
                    </>
                  ) : (
                    <>
                      <span>Sign Up</span>
                      <ChevronRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </button>
                
                <div className="text-center text-sm text-slate-500">
                  Already have an account?{' '}
                  <Link to="/login" className="text-emerald-600 hover:text-emerald-700 font-semibold transition-colors">
                    Login here
                  </Link>
                </div>
              </form>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default RegisterPage;